import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { AsyncSubject, BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { PatientRegistryOpenRequest, PatientRegistryResult } from '../models/patient-registry.model';

/** An opening with a request: what the window reads, and the one answer its caller listens to. */
interface RequestedOpening {
  request: PatientRegistryOpenRequest;
  answer: AsyncSubject<PatientRegistryResult>;
}

/**
 * Open/closed state of the Cadastro de Paciente window (Recepção → Cadastro de Paciente, US 62835).
 *
 * The window is an overlay hosted by `LayoutComponent`, never a route: opening it keeps the page
 * underneath as it is. It closes on `NavigationStart` (Voltar, a link, the expired-session redirect),
 * so it never stays open over another screen and a re-created layout mounts it closed.
 *
 * Opened with a request (US 62981), the window answers once, on the observable {@link open} returned: {@link complete}
 * for a created or uncertain patient, {@link close} for every other exit (cancelled); the observable then completes.
 * Opened by the menu (no request), or while already open, the opening answers nothing: the observable completes empty.
 * A navigation closes it WITHOUT answering: leaving the screen is not cancelling the inclusion.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientRegistryModalService {
  private readonly openState$ = new BehaviorSubject<boolean>(false);
  private opening: RequestedOpening | null = null;

  /** Replays the current state; repeated values are dropped, so a second open() mounts nothing new. */
  readonly open$: Observable<boolean> = this.openState$.pipe(distinctUntilChanged());

  constructor(router: Router) {
    // Root singleton: the subscription lives as long as the application.
    router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.closeWithoutAnswer());
  }

  /** The request of the window on screen; `null` for the window of the menu. */
  get request(): PatientRegistryOpenRequest | null {
    return this.opening?.request ?? null;
  }

  /**
   * Opens the window and returns its one answer. While it is already open nothing changes, the request of the first
   * opening included, and the answer is `EMPTY`; so is the answer of the window of the menu (no request).
   */
  open(request?: PatientRegistryOpenRequest): Observable<PatientRegistryResult> {
    if (this.openState$.value) {
      return EMPTY;
    }
    this.opening = request ? { request, answer: new AsyncSubject<PatientRegistryResult>() } : null;
    this.openState$.next(true);
    return this.opening?.answer.asObservable() ?? EMPTY;
  }

  /** The window asked to close (X, Esc, Cancelar): a window opened with a request answers `cancelled`. */
  close(): void {
    this.end({ kind: 'cancelled' });
    this.openState$.next(false);
  }

  /** The ficha opened with a request ended in a patient (created or uncertain): the answer, and the window closes. */
  complete(result: PatientRegistryResult): void {
    this.end(result);
    this.openState$.next(false);
  }

  /**
   * Ends the opening with a request, once: the request is forgotten before answering, so a close() after it answers
   * nothing; without a result the answer completes empty.
   */
  private end(result?: PatientRegistryResult): void {
    const opening = this.opening;
    if (!opening) {
      return;
    }
    this.opening = null;
    if (result) {
      opening.answer.next(result);
    }
    opening.answer.complete();
  }

  private closeWithoutAnswer(): void {
    this.end();
    this.openState$.next(false);
  }
}
